import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, RefreshIcon } from '../../../../components/icons';
import { Button } from '../../../../components/ui/Button';
import { Card } from '../../../../components/ui/Card';

interface QRCodeScannerProps {
    qrCode: string;
    onCancel: () => void;
    onRefresh: () => void;
    isLoading?: boolean;
    /** Sem Card externo — uso em onboarding. */
    embedded?: boolean;
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
    qrCode,
    onCancel,
    onRefresh,
    embedded = false,
}) => {
    const [dots, setDots] = useState('');

    useEffect(() => {
        const interval = setInterval(() => {
            setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
        }, 500);
        return () => clearInterval(interval);
    }, []);

    const content = (
        <div className="grid w-full max-w-4xl grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
            <div className="space-y-5">
                <div className="rounded-lg border-l-4 border-primary bg-primary/5 px-4 py-3">
                    <p className="text-sm font-semibold text-default">Como conectar seu WhatsApp</p>
                </div>

                <ol className="space-y-3 text-sm leading-relaxed text-default">
                    <li className="flex items-start gap-2">
                        <span className="min-w-[1.25rem] font-semibold text-primary">1.</span>
                        <span>Abra seu WhatsApp no celular</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="min-w-[1.25rem] font-semibold text-primary">2.</span>
                        <span>Toque em <strong>Configurações</strong> → <strong>Aparelhos conectados</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="min-w-[1.25rem] font-semibold text-primary">3.</span>
                        <span>Toque em <strong>Conectar aparelho</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="min-w-[1.25rem] font-semibold text-primary">4.</span>
                        <span>Aponte a câmera para o QR Code</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="min-w-[1.25rem] font-semibold text-primary">5.</span>
                        <span>Aguarde a confirmação de conexão</span>
                    </li>
                </ol>

                <div className="space-y-3 pt-1">
                    <p className="rounded-lg border border-warning/20 bg-warning/10 px-3 py-2.5 text-xs text-warning">
                        Mantenha esta página aberta até a conexão ser confirmada.
                    </p>
                    <Button
                        variant="outline"
                        onClick={onRefresh}
                        className="flex w-full items-center justify-center gap-2"
                    >
                        <RefreshIcon className="h-4 w-4" />
                        Gerar novo QR Code
                    </Button>
                </div>
            </div>

            <div className="flex flex-col items-center gap-4">
                <div className="flex items-center justify-center rounded-xl border border-border bg-card p-5 shadow-sm">
                    {qrCode ? (
                        <div className="relative">
                            <img src={qrCode} alt="QR Code WhatsApp" className="h-56 w-56 object-contain sm:h-64 sm:w-64" />
                            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                                <div className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-primary-foreground shadow-sm">
                                    <span className="h-2 w-2 animate-pulse rounded-full bg-card" />
                                    <span className="text-xs font-medium">Aguardando leitura{dots}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex h-56 w-56 animate-pulse items-center justify-center rounded-lg bg-surface-subtle text-muted sm:h-64 sm:w-64">
                            <div className="text-center">
                                <div className="mx-auto mb-2 h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                                <p className="text-sm">Gerando QR Code...</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 space-y-2 text-center sm:mt-8">
                    <p className="text-sm text-muted">Problemas para conectar?</p>
                    <Button variant="ghost" onClick={onCancel} className="text-danger hover:bg-danger/10 hover:text-danger">
                        Cancelar e tentar novamente
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-lg p-1 text-muted transition-colors hover:bg-surface-subtle hover:text-default"
                    aria-label="Voltar"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <h2 className="text-lg font-semibold text-default sm:text-xl">Conexão com o WhatsApp</h2>
            </div>

            {embedded ? (
                <div className="flex min-h-0 items-center justify-center py-2">{content}</div>
            ) : (
                <Card className="flex min-h-[28rem] items-center justify-center p-6 sm:p-8">{content}</Card>
            )}
        </div>
    );
};

export default QRCodeScanner;
